import { useState, useEffect } from 'react';
import { api, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Brain, Zap, AlertTriangle, TrendingDown, Check, Send, Loader2, ChevronDown, ChevronUp, Target, Activity, ArrowDown, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const PRIORITY_COLORS = {
  haute: { bg: 'rgb(var(--lm-red) / 0.06)', border: 'rgb(var(--lm-red) / 0.2)', text: 'rgb(var(--lm-red))' },
  moyenne: { bg: 'rgb(var(--lm-orange) / 0.06)', border: 'rgb(var(--lm-orange) / 0.2)', text: 'rgb(var(--lm-orange))' },
  basse: { bg: 'rgb(var(--lm-green) / 0.06)', border: 'rgb(var(--lm-green) / 0.2)', text: 'rgb(var(--lm-green))' },
};

const CATEGORY_LABELS = {
  charges_fixes: 'Charges fixes',
  salaires: 'Salaires',
  achats: 'Achats',
  pertes: 'Pertes',
  ca: 'Chiffre d\'affaires',
  carte: 'Carte & Prix',
};

const Optimisation = () => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [sendingTelegram, setSendingTelegram] = useState(false);
  const [expandedOptim, setExpandedOptim] = useState(null);
  const [showCharges, setShowCharges] = useState(false);

  const fetchLastAnalysis = async () => {
    try {
      const res = await api.get('/optimisation/dernier');
      setAnalysis(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchLastAnalysis(); }, []);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post('/optimisation/analyser', {}, { timeout: 120000 });
      setAnalysis(res.data);
      toast.success('Analyse terminee');
    } catch (err) {
      toast.error('Erreur analyse IA');
      console.error(err);
    }
    setAnalyzing(false);
  };

  const sendTelegram = async () => {
    setSendingTelegram(true);
    try {
      const res = await api.post('/optimisation/telegram');
      toast.success(`Rapport envoye — Economies: ${formatCurrency(res.data.economies_totales)}/mois`);
    } catch { toast.error('Erreur envoi Telegram'); }
    setSendingTelegram(false);
  };

  const result = analysis?.result;
  const totaux = analysis?.totaux;

  const scoreColor = (score) => {
    if (score >= 70) return 'rgb(var(--lm-green))';
    if (score >= 50) return 'rgb(var(--lm-orange))';
    return 'rgb(var(--lm-red))';
  };

  const totalEconomies = result?.optimisations?.reduce((s, o) => s + (o.economie_estimee || 0), 0) || 0;

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 gap-4">
      <Activity size={28} className="animate-pulse-slow" style={{ color: 'rgb(var(--lm-gold))' }} />
      <span className="text-xs tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">Chargement</span>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="optimisation-page">
      {/* Header */}
      <div className="flex items-end justify-between animate-slide-up">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Intelligence artificielle</p>
          <h1 className="font-title text-2xl text-[rgb(var(--lm-text))] flex items-center gap-2">
            <Brain size={20} style={{ color: 'rgb(var(--lm-gold))' }} />
            Optimisation
          </h1>
        </div>
        <div className="flex gap-2">
          {result && (
            <Button onClick={sendTelegram} disabled={sendingTelegram} variant="ghost" size="sm"
              className="text-[rgb(var(--lm-gold))] hover:bg-[rgb(var(--lm-gold)/0.1)]" data-testid="send-telegram-btn">
              {sendingTelegram ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
              Telegram
            </Button>
          )}
          <Button onClick={runAnalysis} disabled={analyzing}
            className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-accent-text))] hover:opacity-90" data-testid="run-analysis-btn">
            {analyzing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Zap size={14} className="mr-2" />}
            {analyzing ? 'Analyse en cours...' : result ? 'Relancer l\'analyse' : 'Lancer l\'analyse IA'}
          </Button>
        </div>
      </div>

      {/* No analysis yet */}
      {!result && !analyzing && (
        <div className="lm-card p-12 text-center animate-slide-up" style={{ animationDelay: '60ms', animationFillMode: 'backwards' }}>
          <Brain size={40} className="mx-auto mb-4 text-[rgb(var(--lm-text-muted))]" />
          {analysis?.mode_ca === 'aucun' ? (
            <>
              <p className="text-sm text-[rgb(var(--lm-text))] font-medium">Pas de donnees CA</p>
              <p className="text-xs text-[rgb(var(--lm-text-secondary))] mt-2 max-w-md mx-auto">
                Saisissez d'abord votre chiffre d'affaires dans <b>"Saisie CA"</b> pour que l'IA puisse analyser votre situation financiere.
              </p>
              <div className="mt-4 grid grid-cols-3 gap-3 max-w-md mx-auto">
                <div className="p-3 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))]">
                  <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Charges fixes</p>
                  <p className="font-mono text-lg text-[rgb(var(--lm-text))]">{analysis?.totaux?.charges_fixes?.toLocaleString('fr-FR')} EUR</p>
                </div>
                <div className="p-3 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))]">
                  <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Salaires</p>
                  <p className="font-mono text-lg text-[rgb(var(--lm-text))]">{analysis?.totaux?.salaires?.toLocaleString('fr-FR')} EUR</p>
                </div>
                <div className="p-3 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))]">
                  <p className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase">Achats</p>
                  <p className="font-mono text-lg text-[rgb(var(--lm-text))]">{analysis?.totaux?.achats?.toLocaleString('fr-FR')} EUR</p>
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-[rgb(var(--lm-text-secondary))]">Aucune analyse disponible</p>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-2">L'IA va analyser vos charges fixes, salaires, achats et marges pour identifier les economies possibles</p>
            </>
          )}
        </div>
      )}

      {/* Analyzing animation */}
      {analyzing && (
        <div className="lm-card p-12 text-center animate-slide-up">
          <Brain size={40} className="mx-auto mb-4 animate-pulse" style={{ color: 'rgb(var(--lm-gold))' }} />
          <p className="text-sm text-[rgb(var(--lm-text))]">Analyse en cours...</p>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-2">L'IA examine vos donnees financieres. Cela peut prendre 30 secondes.</p>
        </div>
      )}

      {/* Results */}
      {result && !analyzing && (
        <>
          {/* Mode CA banner */}
          {analysis?.mode_ca && analysis.mode_ca !== 'reel' && (
            <div className={`lm-card p-3 flex items-center gap-2 animate-slide-up ${
              analysis.mode_ca === 'fallback' ? 'border-orange-400/30' : 'border-blue-400/30'
            }`} data-testid="ca-mode-banner">
              <Activity size={14} className={analysis.mode_ca === 'fallback' ? 'text-orange-400' : 'text-blue-400'} />
              <p className="text-xs text-[rgb(var(--lm-text-secondary))]">
                {analysis.mode_ca === 'fallback' && (
                  <>Pas de CA en cours — analyse basee sur <b>{analysis.mois_affiche}</b> (dernier mois avec donnees)</>
                )}
                {analysis.mode_ca === 'projection' && (
                  <>Projection : <b>{analysis.jours_saisis} jours</b> saisis — CA extrapole sur le mois entier</>
                )}
              </p>
            </div>
          )}

          {/* Score + Resume */}
          <div className="lm-card p-6 animate-slide-up" style={{ animationDelay: '60ms', animationFillMode: 'backwards', borderColor: `${scoreColor(result.score_sante)} / 0.3` }} data-testid="score-card">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-2">Score sante financiere</p>
                <p className="font-mono text-5xl font-light" style={{ color: scoreColor(result.score_sante) }}>
                  {result.score_sante}<span className="text-xl text-[rgb(var(--lm-text-muted))]">/100</span>
                </p>
                <p className="text-sm text-[rgb(var(--lm-text-secondary))] mt-3 leading-relaxed">{result.resume}</p>
              </div>
              {totalEconomies > 0 && (
                <div className="text-right ml-6 shrink-0">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Economies possibles</p>
                  <p className="font-mono text-2xl" style={{ color: 'rgb(var(--lm-green))' }}>
                    -{formatCurrency(totalEconomies)}
                  </p>
                  <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">par mois</p>
                </div>
              )}
            </div>
          </div>

          {/* Indicateurs */}
          {result.indicateurs && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Charges fixes', val: result.indicateurs.ratio_charges_fixes },
                { label: 'Salaires', val: result.indicateurs.ratio_salaires },
                { label: 'Achats', val: result.indicateurs.ratio_achats },
              ].map((item, idx) => {
                const sane = item.val != null && item.val <= 999 && totaux?.ca_ht > 0;
                return (
                  <div key={item.label} className="lm-card p-4 text-center animate-slide-up" style={{ animationDelay: `${120 + idx * 60}ms`, animationFillMode: 'backwards' }}>
                    <p className="kpi-label">{item.label}</p>
                    <p className="font-mono text-lg text-[rgb(var(--lm-text))]">{sane ? `${item.val}%` : '--'}</p>
                    <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">du CA HT</p>
                  </div>
                );
              })}
              <div className="lm-card p-4 text-center animate-slide-up" style={{ animationDelay: '300ms', animationFillMode: 'backwards' }}>
                <p className="kpi-label">Marge nette</p>
                <p className={`font-mono text-lg ${!totaux?.ca_ht ? 'text-[rgb(var(--lm-text-muted))]' : result.indicateurs.marge_nette_pct >= 0 ? 'text-[rgb(var(--lm-green))]' : 'text-[rgb(var(--lm-red))]'}`}>
                  {totaux?.ca_ht > 0 ? `${result.indicateurs.marge_nette_pct}%` : '--'}
                </p>
              </div>
            </div>
          )}

          {/* Alertes urgentes */}
          {result.alertes_urgentes?.length > 0 && (
            <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '360ms', animationFillMode: 'backwards', borderColor: 'rgb(var(--lm-red) / 0.3)' }} data-testid="alertes-urgentes">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={16} style={{ color: 'rgb(var(--lm-red))' }} />
                <span className="text-sm font-medium" style={{ color: 'rgb(var(--lm-red))' }}>Alertes urgentes</span>
              </div>
              <div className="space-y-2">
                {result.alertes_urgentes.map((a, i) => (
                  <div key={i} className="flex justify-between items-center p-2.5 bg-[rgb(var(--lm-red)/0.04)] rounded-sm">
                    <span className="text-sm text-[rgb(var(--lm-text))]">{a.message}</span>
                    {a.impact > 0 && <span className="font-mono text-xs" style={{ color: 'rgb(var(--lm-red))' }}>-{formatCurrency(a.impact)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Optimisations */}
          <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '420ms', animationFillMode: 'backwards' }} data-testid="optimisations-list">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrendingDown size={16} style={{ color: 'rgb(var(--lm-green))' }} />
                <span className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Optimisations detectees</span>
              </div>
              <span className="font-mono text-sm" style={{ color: 'rgb(var(--lm-green))' }}>
                {result.optimisations?.length || 0} piste(s)
              </span>
            </div>
            <div className="space-y-2">
              {result.optimisations?.map((o, i) => {
                const prio = PRIORITY_COLORS[o.priorite] || PRIORITY_COLORS.basse;
                const isExpanded = expandedOptim === i;
                return (
                  <div key={i} className="rounded-sm overflow-hidden" style={{ borderLeft: `3px solid ${prio.text}` }}>
                    <button className="w-full flex items-center justify-between p-3 bg-[rgb(var(--lm-surface-elevated))] hover:bg-[rgb(var(--lm-surface-elevated)/0.8)] transition-colors text-left"
                      onClick={() => setExpandedOptim(isExpanded ? null : i)} data-testid={`optim-${i}`}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-medium shrink-0"
                          style={{ color: prio.text, background: prio.bg }}>{o.priorite}</span>
                        <span className="text-sm text-[rgb(var(--lm-text))] truncate">{o.titre}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))] shrink-0">
                          {CATEGORY_LABELS[o.categorie] || o.categorie}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        {o.economie_estimee > 0 && (
                          <span className="font-mono text-sm" style={{ color: 'rgb(var(--lm-green))' }}>
                            <ArrowDown size={10} className="inline mr-0.5" />{formatCurrency(o.economie_estimee)}/mois
                          </span>
                        )}
                        {isExpanded ? <ChevronUp size={14} className="text-[rgb(var(--lm-text-muted))]" /> : <ChevronDown size={14} className="text-[rgb(var(--lm-text-muted))]" />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="p-3 bg-[rgb(var(--lm-bg))] space-y-2 animate-slide-up">
                        <p className="text-sm text-[rgb(var(--lm-text-secondary))] leading-relaxed">{o.detail}</p>
                        <div className="flex items-start gap-2 mt-2 p-2 bg-[rgb(var(--lm-surface-elevated))] rounded-sm">
                          <Target size={12} className="mt-0.5 shrink-0" style={{ color: 'rgb(var(--lm-gold))' }} />
                          <p className="text-xs text-[rgb(var(--lm-text))]"><span className="font-medium">Action :</span> {o.action}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Charges fixes analysis */}
          {result.charges_fixes_analyse?.length > 0 && (
            <div className="lm-card overflow-hidden animate-slide-up" style={{ animationDelay: '480ms', animationFillMode: 'backwards' }}>
              <button className="w-full flex items-center justify-between p-5 hover:bg-[rgb(var(--lm-surface-elevated)/0.3)] transition-colors"
                onClick={() => setShowCharges(!showCharges)} data-testid="toggle-charges-detail">
                <span className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Detail charges fixes</span>
                {showCharges ? <ChevronUp size={16} className="text-[rgb(var(--lm-text-muted))]" /> : <ChevronDown size={16} className="text-[rgb(var(--lm-text-muted))]" />}
              </button>
              {showCharges && (
                <div className="border-t border-[rgb(var(--lm-border))]">
                  {result.charges_fixes_analyse.map((c, i) => {
                    const verdictColors = {
                      normal: 'rgb(var(--lm-green))',
                      eleve: 'rgb(var(--lm-red))',
                      a_verifier: 'rgb(var(--lm-orange))',
                      optimisable: 'rgb(var(--lm-orange))',
                    };
                    return (
                      <div key={i} className="flex items-center justify-between px-5 py-3 border-b border-[rgb(var(--lm-border)/0.5)] last:border-0">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[rgb(var(--lm-text))] truncate">{c.libelle}</p>
                          {c.conseil && <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-0.5">{c.conseil}</p>}
                        </div>
                        <div className="flex items-center gap-3 shrink-0 ml-3">
                          <span className="font-mono text-sm text-[rgb(var(--lm-text))]">{formatCurrency(c.montant)}</span>
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                            style={{ color: verdictColors[c.verdict] || 'rgb(var(--lm-text-muted))', background: `${verdictColors[c.verdict] || 'rgb(var(--lm-text-muted))'} / 0.08` }}>
                            {c.verdict}
                          </span>
                          {c.economie_possible > 0 && (
                            <span className="font-mono text-xs" style={{ color: 'rgb(var(--lm-green))' }}>-{formatCurrency(c.economie_possible)}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Objectifs */}
          {result.objectifs_mois_prochain?.length > 0 && (
            <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '540ms', animationFillMode: 'backwards', borderColor: 'rgb(var(--lm-gold) / 0.2)' }} data-testid="objectifs">
              <div className="flex items-center gap-2 mb-3">
                <Target size={16} style={{ color: 'rgb(var(--lm-gold))' }} />
                <span className="text-xs tracking-[0.15em] uppercase text-[rgb(var(--lm-text-secondary))]">Objectifs mois prochain</span>
              </div>
              <div className="space-y-2">
                {result.objectifs_mois_prochain.map((obj, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Check size={12} className="mt-1 shrink-0" style={{ color: 'rgb(var(--lm-gold))' }} />
                    <p className="text-sm text-[rgb(var(--lm-text-secondary))]">{obj}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Metadata */}
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] text-center">
            Analyse du {new Date(analysis.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            {totaux && ` — CA HT: ${formatCurrency(totaux.ca_ht || totaux.ca_ttc)}, Fixes: ${formatCurrency(totaux.charges_fixes)}, Salaires: ${formatCurrency(totaux.salaires)}`}
          </p>
        </>
      )}
    </div>
  );
};

export default Optimisation;
